import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, ClipboardList, Pencil, Check, X } from "lucide-react";
import { Avatar } from "@/components/common";
import { GuestTermsList, RequestDialog } from "@/components/plan/RequestDialog";
import { api, apiError } from "@/lib/api";

const RequestRow = ({ r, onChange }) => {
  const respond = async (accept) => {
    try {
      await api.post(`/requests/${r.id}/respond`, { accept });
      toast.success(accept ? `${r.user?.name} added to the ${r.kind === "cohost" ? "circle" : "guest list"}` : "Request declined");
      onChange();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid={`pending-request-${r.id}`}>
      <Link to={`/members/${r.user?.id}`} className="flex items-center gap-3 hover:text-primary">
        <Avatar person={r.user} size={40} />
        <div><p className="font-semibold">{r.user?.name}</p><p className="text-xs text-muted-foreground">{r.invited ? "Invited" : "Requests"} {r.kind === "cohost" ? "to co-host" : "a guest place"}</p></div>
      </Link>
      {r.message && <p className="text-sm text-muted-foreground">“{r.message}”</p>}
      {r.invited ? <p className="text-xs text-muted-foreground">Awaiting their reply</p> : (
        <div className="flex gap-2">
          <button type="button" className="btn-primary min-h-[44px] flex-1 px-3" onClick={() => respond(true)} data-testid={`accept-request-${r.id}`}><Check className="h-4 w-4" /> Accept</button>
          <button type="button" className="btn-outline min-h-[44px] flex-1 px-3" onClick={() => respond(false)} data-testid={`decline-request-${r.id}`}><X className="h-4 w-4" /> Decline</button>
        </div>
      )}
    </li>
  );
};

const CircleActions = ({ exp, onChange }) => {
  const navigate = useNavigate();
  const role = exp.my_role;
  const leave = async () => {
    if (!window.confirm("Leave this experience?")) return;
    try { await api.post(`/experiences/${exp.id}/leave`); toast.success("You left the experience"); navigate("/circle"); } catch (e) { toast.error(apiError(e)); }
  };
  const ack = async () => {
    try { const { data } = await api.post(`/experiences/${exp.id}/guest-ack`); onChange(data); toast.success("Guest terms acknowledged"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      {role !== "guest" && <Link to={`/experiences/${exp.id}/plan`} className="btn-primary w-full" data-testid="open-planning-button"><ClipboardList className="h-4 w-4" /> Open planning space</Link>}
      {exp.thread_id && <Link to={`/messages/${exp.thread_id}`} className="btn-outline w-full" data-testid="open-circle-chat-button"><MessageSquare className="h-4 w-4" /> Circle conversation</Link>}
      {role === "lead" && <Link to={`/experiences/${exp.id}/edit`} className="btn-outline w-full" data-testid="edit-experience-button"><Pencil className="h-4 w-4" /> Edit experience</Link>}
      {role === "guest" && !exp.guest_ack_current && (
        <div className="rounded-lg border border-[hsl(var(--gold))]/50 p-4 text-sm" data-testid="guest-terms-changed">
          <p>Guest terms changed since you joined. Review them below and acknowledge the latest version.</p>
          <button type="button" className="btn-primary mt-3 min-h-[44px] w-full" onClick={ack} data-testid="guest-ack-button">Acknowledge version {exp.terms_version}</button>
        </div>
      )}
      {(role === "cohost" || role === "guest") && <button type="button" className="min-h-[44px] w-full text-sm text-muted-foreground underline underline-offset-4 hover:text-destructive" onClick={leave} data-testid="leave-experience-button">Leave this experience</button>}
    </div>
  );
};

const JoinActions = ({ exp, onChange }) => {
  const [dialog, setDialog] = useState(null);
  const open = ["finding_cohosts", "proposed_terms"].includes(exp.status);
  const guestOpen = ["finding_cohosts", "proposed_terms", "agreed"].includes(exp.status) && exp.guest_count < exp.guest_places;
  const req = exp.my_request;
  const withdraw = async () => {
    try { await api.post(`/requests/${req.id}/withdraw`); toast.success("Request withdrawn"); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  const decline = async () => {
    try { await api.post(`/requests/${req.id}/respond`, { accept: false }); toast.success("Invitation declined"); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  if (exp.is_sample) return (
    <div className="space-y-3">
      <button type="button" className="btn-primary w-full" disabled data-testid="request-cohost-button">Request to co-host</button>
      <button type="button" className="btn-outline w-full" disabled data-testid="request-guest-button">Request guest place</button>
      <p className="text-xs text-muted-foreground" data-testid="sample-disabled-note">This is an illustrative concept. Requests are available on member-created experiences.</p>
    </div>
  );
  if (req) return req.invited ? (
    <div className="space-y-3" data-testid="invitation-panel">
      <p className="text-sm">You've been invited {req.kind === "cohost" ? "to co-host" : "as a guest"}.</p>
      <button type="button" className="btn-primary w-full" onClick={() => setDialog(req.kind)} data-testid="accept-invite-button">Review & accept</button>
      <button type="button" className="btn-outline w-full" onClick={decline} data-testid="decline-invite-button">Decline</button>
      {dialog && <RequestDialog open onOpenChange={() => setDialog(null)} exp={exp} kind={dialog} requestId={req.id} onDone={() => onChange()} />}
    </div>
  ) : (
    <div className="space-y-3" data-testid="pending-request-panel">
      <p className="rounded-lg bg-secondary p-4 text-sm">Your request {req.kind === "cohost" ? "to co-host" : "for a guest place"} is with the lead host.</p>
      <button type="button" className="btn-outline w-full" onClick={withdraw} data-testid="withdraw-request-button">Withdraw request</button>
    </div>
  );
  return (
    <div className="space-y-3">
      <button type="button" className="btn-primary w-full" disabled={!open} onClick={() => setDialog("cohost")} data-testid="request-cohost-button">Request to co-host</button>
      <button type="button" className="btn-outline w-full" disabled={!guestOpen} onClick={() => setDialog("guest")} data-testid="request-guest-button">Request guest place</button>
      {!guestOpen && <p className="text-xs text-muted-foreground">{exp.guest_places ? "All guest places are filled." : "No guest places offered."}</p>}
      {dialog && <RequestDialog open onOpenChange={() => setDialog(null)} exp={exp} kind={dialog} onDone={onChange} />}
    </div>
  );
};

export const ActionPanel = ({ exp, onChange }) => (
  <aside className="surface overflow-hidden border-primary/50 lg:sticky lg:top-24" data-testid="experience-action-panel">
    <div className="p-6 pb-4"><h2 className="font-display text-3xl">Join the conversation</h2></div>
    <img src="/brand/angels-v2.webp" alt="" aria-hidden className="hidden aspect-[4/3] w-full object-cover object-[center_12%] lg:block" />
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span>{exp.cohost_count} co-host{exp.cohost_count === 1 ? "" : "s"} · {exp.guest_count}/{exp.guest_places} guests</span>
        <span className="text-muted-foreground">Contributions agreed individually</span>
      </div>
      {exp.my_role ? <CircleActions exp={exp} onChange={onChange} /> : <JoinActions exp={exp} onChange={onChange} />}
      {exp.pending_requests?.length > 0 && (
        <div>
          <h3 className="mb-3 font-semibold">Requests & invitations ({exp.pending_requests.length})</h3>
          <ul className="space-y-3">{exp.pending_requests.map((r) => <RequestRow key={r.id} r={r} onChange={onChange} />)}</ul>
        </div>
      )}
      <div className="hairline" />
      <div>
        <h3 className="font-display text-2xl">Guest costs</h3>
        <p className="mt-2 text-sm text-foreground/85">Covered costs and personal extras are shown before you commit.</p>
        <div className="mt-4"><GuestTermsList terms={exp.guest_terms} compact /></div>
      </div>
      <span className="block h-px w-10 bg-foreground/30" />
      <p className="text-sm text-muted-foreground">Bookings and payments are arranged outside the platform.</p>
    </div>
  </aside>
);
